import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPickupLocations, createOrder } from '../services/api';
import { useCart } from '../context/CartContext';
import { PickupLocation, Order } from '../types';
import CartItem from '../components/CartItem';
import './OrderPage.css';

const OrderPage: React.FC = () => {
  const navigate = useNavigate();
  const { cartItems, getTotalAmount, clearCart } = useCart();
  
  const [pickupLocations, setPickupLocations] = useState<PickupLocation[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [customerName, setCustomerName] = useState<string>('');
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [pickupDate, setPickupDate] = useState<string>('');
  
  // Form validation
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  
  useEffect(() => {
    const fetchPickupLocations = async () => {
      try {
        setLoading(true);
        const data = await getPickupLocations();
        setPickupLocations(data);
      } catch (err) {
        console.error('Error fetching pickup locations:', err);
        // Mock data for demonstration
        const mockLocations: PickupLocation[] = [
          {
            _id: '1', // Use string for _id
            id: 1,    // Use number for id
            name: 'Belváros',
            address: '1052 Budapest, Petőfi utca 10.',
            openingHours: 'H-P: 7:00-19:00, Szo: 8:00-14:00',
            available: true
          },
          {
            _id: '2', // Use string for _id
            id: 2,    // Use number for id
            name: 'Újbuda',
            address: '1117 Budapest, Móricz Zsigmond körtér 3.',
            openingHours: 'H-P: 7:00-19:00, Szo: 8:00-14:00',
            available: true
          },
          {
            _id: '3', // Use string for _id
            id: 3,    // Use number for id
            name: 'Óbuda',
            address: '1036 Budapest, Lajos utca 45.',
            openingHours: 'H-P: 7:00-19:00, Szo: 8:00-14:00',
            available: true
          }
        ];
        setPickupLocations(mockLocations);
      } finally {
        setLoading(false);
      }
    };

    fetchPickupLocations();
  }, []);
  
  // Get tomorrow's date as the minimum date for pickup
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };
  
  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!customerName.trim()) {
      errors.customerName = 'A név megadása kötelező';
    }
    
    if (!customerEmail.trim()) {
      errors.customerEmail = 'Az email cím megadása kötelező';
    } else if (!/\S+@\S+\.\S+/.test(customerEmail)) {
      errors.customerEmail = 'Érvénytelen email cím';
    }
    
    if (!customerPhone.trim()) {
      errors.customerPhone = 'A telefonszám megadása kötelező';
    }
    
    if (!selectedLocation) {
      errors.selectedLocation = 'Kérjük, válassz átvételi pontot';
    }
    
    if (!pickupDate) {
      errors.pickupDate = 'Kérjük, válassz átvételi dátumot';
    }
    
    if (cartItems.length === 0) {
      errors.cartItems = 'A kosár üres';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Ensure we have a valid location ID
      // If selectedLocation is not a valid number, use a default value (1)
      let locationId;
      if (selectedLocation) {
        const parsedId = parseInt(selectedLocation, 10);
        locationId = isNaN(parsedId) ? 1 : parsedId; // Use 1 as a fallback if parsing fails
      } else {
        locationId = 1; // Default to 1 if no location is selected
      }
      
      // Prepare order data with proper typing
      const orderData: Omit<Order, '_id'> = {
        customerName,
        customerEmail,
        customerPhone,
        items: cartItems.map(item => ({
          product: item.product._id || item.product.id?.toString() || '',
          productName: item.product.name,
          productPrice: item.product.price,
          quantity: item.quantity
        })),
        // Use pickupLocationId (camelCase) to match backend expectations
        pickupLocationId: locationId, // Use the converted value
        pickupDate: new Date(pickupDate).toISOString(),
        totalAmount: getTotalAmount()
      };
      
      // Send the order to the backend
      const response = await createOrder(orderData);
      console.log('Order created successfully:', response);
      
      // Clear the cart and navigate to confirmation page
      clearCart();
      navigate('/order-confirmation', { 
        state: { 
          orderId: response.orderId,
          customerName: response.customerName,
          pickupDate: response.pickupDate,
          totalAmount: response.totalAmount
        } 
      });
      
    } catch (err) {
      console.error('Error creating order:', err);
      setError('Hiba történt a rendelés leadása során. Kérjük, próbáld újra később.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="order-page">
      <h1>Rendelés leadása</h1>
      
      {cartItems.length === 0 ? (
        <div className="empty-cart">
          <p>A kosár üres.</p>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/products')}
          >
            Termékek böngészése
          </button>
        </div>
      ) : (
        <div className="order-content">
          <div className="cart-section">
            <h2>Kosár tartalma</h2>
            <div className="cart-items">
              {cartItems.map((item) => (
                <CartItem key={item.product._id} item={item} />
              ))}
            </div>
            <div className="cart-total">
              <span>Összesen:</span>
              <span className="total-amount">{getTotalAmount().toLocaleString('hu-HU')} Ft</span>
            </div>
          </div>
          
          <div className="order-form-section">
            <h2>Rendelési adatok</h2>
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleSubmit} className="order-form">
              <div className="form-group">
                <label htmlFor="customerName" className="form-label">Név</label>
                <input
                  type="text"
                  id="customerName"
                  className={`form-control ${formErrors.customerName ? 'is-invalid' : ''}`}
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                />
                {formErrors.customerName && (
                  <div className="invalid-feedback">{formErrors.customerName}</div>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="customerEmail" className="form-label">Email cím</label>
                <input
                  type="email"
                  id="customerEmail"
                  className={`form-control ${formErrors.customerEmail ? 'is-invalid' : ''}`}
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                />
                {formErrors.customerEmail && (
                  <div className="invalid-feedback">{formErrors.customerEmail}</div>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="customerPhone" className="form-label">Telefonszám</label>
                <input
                  type="tel"
                  id="customerPhone"
                  className={`form-control ${formErrors.customerPhone ? 'is-invalid' : ''}`}
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                />
                {formErrors.customerPhone && (
                  <div className="invalid-feedback">{formErrors.customerPhone}</div>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="pickupLocation" className="form-label">Átvételi pont</label>
                <select
                  id="pickupLocation"
                  className={`form-control ${formErrors.selectedLocation ? 'is-invalid' : ''}`}
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                >
                  <option key="default" value="">Válassz átvételi pontot</option>
                  {pickupLocations.map((location) => (
                    <option key={location._id} value={location._id}>
                      {location.name} - {location.address}
                    </option>
                  ))}
                </select>
                {formErrors.selectedLocation && (
                  <div className="invalid-feedback">{formErrors.selectedLocation}</div>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="pickupDate" className="form-label">Átvétel dátuma</label>
                <input
                  type="date"
                  id="pickupDate"
                  className={`form-control ${formErrors.pickupDate ? 'is-invalid' : ''}`}
                  min={getTomorrowDate()}
                  value={pickupDate}
                  onChange={(e) => setPickupDate(e.target.value)}
                />
                {formErrors.pickupDate && (
                  <div className="invalid-feedback">{formErrors.pickupDate}</div>
                )}
              </div>
              
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate('/products')}
                >
                  Vissza a termékekhez
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Feldolgozás...' : 'Rendelés leadása'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderPage;
